// web/src/store/marketData/marketDataSlice.ts
import {
  downloadCommodityForwardCurveReport,
  reportFxForwardCurves,
} from '@/pages/MarketCurvesCalculation/store/thunks';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
  checkCommodityAvailability,
  currenciesMarketData,
  downloadCDSCurveReport,
  downloadHairyChartReport,
  downloadHistoricalForwardCurveReport,
  downloadIrDifferencialCurrencyReport,
  downloadIrForwardCurveReport,
  emailIrForwardCurveReport,
  fetchClosestTradeValuationDate,
  fetchCommoditiesIndices,
  fetchIRDifferential,
  fetchIRTimeFrequencies,
  fetchIrIndexList,
  fetchSecurities,
  filteredCurrenciesMarketData,
  validateIrIndex,
} from './marketDataThunks';

// Define the FilterOption interface if not defined elsewhere
interface FilterOption {
  label: string;
  value: string;
}

interface GeneralState {
  data: any | null;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  errorMessage: string | null;
}

type GeneralStateAndFileS3 = GeneralState & {
  data: Array<{
    file_s3_url: string;
    file_name: string;
  }> | null;
};

interface MarketDataState {
  irIndexList: GeneralState;
  closestTradeValDate: GeneralState & { value_date?: string };
  validateIrIndex: { [key: string]: boolean } | null;
  downloadIrForwardCurveReport: GeneralState;
  emailIrForwardCurveReport: GeneralState;
  currenciesMarketData: GeneralState;
  filteredCurrencies: GeneralState;
  irDifferential: GeneralState;
  commodityIndexList: GeneralState;
  commodityAvailability: GeneralState;
  timeFrequencies: GeneralState;
  securities: GeneralState & { data: { id: string; label: string }[] };
  cdsCurveReport: GeneralStateAndFileS3;
  irDifferencialCurrencyReport: GeneralStateAndFileS3;
  historicalForwardCurveReport: GeneralStateAndFileS3;
  hairyChartReport: GeneralStateAndFileS3;
  fxForwardCurves: GeneralStateAndFileS3;
  commodityForwardCurveReport: GeneralStateAndFileS3;
}

const initialMarketDataState: MarketDataState = {
  irIndexList: {
    data: null,
    loading: false,
    error: null,
    successMessage: null,
    errorMessage: null,
  },
  closestTradeValDate: {
    data: null,
    loading: false,
    error: null,
    successMessage: null,
    errorMessage: null,
  },
  validateIrIndex: null,
  downloadIrForwardCurveReport: {
    data: null,
    loading: false,
    error: null,
    successMessage: null,
    errorMessage: null,
  },
  emailIrForwardCurveReport: {
    data: null,
    loading: false,
    error: null,
    successMessage: null,
    errorMessage: null,
  },
  currenciesMarketData: {
    data: null,
    loading: false,
    error: null,
    successMessage: null,
    errorMessage: null,
  },
  filteredCurrencies: {
    data: [],
    loading: false,
    error: null,
    successMessage: null,
    errorMessage: null,
  },
  irDifferential: {
    data: null,
    loading: false,
    error: null,
    successMessage: null,
    errorMessage: null,
  },
  commodityIndexList: {
    data: null,
    loading: false,
    error: null,
    successMessage: null,
    errorMessage: null,
  },
  commodityAvailability: {
    data: null,
    loading: false,
    error: null,
    successMessage: null,
    errorMessage: null,
  },
  timeFrequencies: {
    data: null,
    loading: false,
    error: null,
    successMessage: null,
    errorMessage: null,
  },
  securities: {
    data: [],
    loading: false,
    error: null,
    successMessage: null,
    errorMessage: null,
  },
  cdsCurveReport: {
    data: null,
    loading: false,
    error: null,
    successMessage: null,
    errorMessage: null,
  },
  irDifferencialCurrencyReport: {
    data: null,
    loading: false,
    error: null,
    successMessage: null,
    errorMessage: null,
  },
  historicalForwardCurveReport: {
    data: null,
    loading: false,
    error: null,
    successMessage: null,
    errorMessage: null,
  },
  hairyChartReport: {
    data: null,
    loading: false,
    error: null,
    successMessage: null,
    errorMessage: null,
  },
  fxForwardCurves: {
    data: null,
    loading: false,
    error: null,
    successMessage: null,
    errorMessage: null,
  },
  commodityForwardCurveReport: {
    data: null,
    loading: false,
    error: null,
    successMessage: null,
    errorMessage: null,
  },
};

const marketDataSlice = createSlice({
  name: 'marketData',
  initialState: initialMarketDataState,
  reducers: {
    // You can add synchronous reducers here if needed
    clearValidation(state) {
      state.validateIrIndex = null;
    },
    clearSuccessMessages(state) {
      state.downloadIrForwardCurveReport.successMessage = null;
      state.emailIrForwardCurveReport.successMessage = null;
    },
    clearErrorMessages(state) {
      state.downloadIrForwardCurveReport.errorMessage = null;
      state.emailIrForwardCurveReport.errorMessage = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch IR Index List
    builder
      .addCase(fetchIrIndexList.pending, (state) => {
        state.irIndexList.loading = true;
        state.irIndexList.error = null;
      })
      .addCase(
        fetchIrIndexList.fulfilled,
        (state, action: PayloadAction<FilterOption[]>) => {
          state.irIndexList.loading = false;
          state.irIndexList.data = action.payload;
        },
      )
      .addCase(fetchIrIndexList.rejected, (state, action) => {
        state.irIndexList.loading = false;
        state.irIndexList.error =
          action.payload || 'Failed to fetch IR Index List';
      });

    // Fetch Closest Trade Valuation Date
    builder
      .addCase(fetchClosestTradeValuationDate.pending, (state) => {
        state.closestTradeValDate.loading = true;
        state.closestTradeValDate.error = null;
      })
      .addCase(
        fetchClosestTradeValuationDate.fulfilled,
        (state, action: PayloadAction<{ value_date: string }>) => {
          state.closestTradeValDate.loading = false;
          state.closestTradeValDate.data = action.payload;
          state.closestTradeValDate.value_date = action.payload.value_date;
        },
      )
      .addCase(fetchClosestTradeValuationDate.rejected, (state, action) => {
        state.closestTradeValDate.loading = false;
        state.closestTradeValDate.error =
          action.payload || 'Failed to fetch Closest Trade Valuation Date';
      });

    // Validate IR Index
    builder
      .addCase(validateIrIndex.pending, (state) => {
        state.validateIrIndex = null;
      })
      .addCase(
        validateIrIndex.fulfilled,
        (state, action: PayloadAction<{ [key: string]: boolean }>) => {
          state.validateIrIndex = action.payload;
        },
      )
      .addCase(validateIrIndex.rejected, (state, action) => {
        state.validateIrIndex = null;
      });

    // Download IR Forward Curve Report
    builder
      .addCase(downloadIrForwardCurveReport.pending, (state) => {
        state.downloadIrForwardCurveReport.loading = true;
        state.downloadIrForwardCurveReport.error = null;
        state.downloadIrForwardCurveReport.successMessage = null;
      })
      .addCase(
        downloadIrForwardCurveReport.fulfilled,
        (
          state,
          action: PayloadAction<{ file_s3_url: string; file_name: string }>,
        ) => {
          state.downloadIrForwardCurveReport.loading = false;
          state.downloadIrForwardCurveReport.data = action.payload;
          state.downloadIrForwardCurveReport.successMessage =
            'Report downloaded successfully';
        },
      )
      .addCase(downloadIrForwardCurveReport.rejected, (state, action) => {
        state.downloadIrForwardCurveReport.loading = false;
        state.downloadIrForwardCurveReport.error =
          action.payload || 'Failed to download IR Forward Curve Report';
      });

    // Email IR Forward Curve Report
    builder
      .addCase(emailIrForwardCurveReport.pending, (state) => {
        state.emailIrForwardCurveReport.loading = true;
        state.emailIrForwardCurveReport.error = null;
        state.emailIrForwardCurveReport.successMessage = null;
      })
      .addCase(
        emailIrForwardCurveReport.fulfilled,
        (state, action: PayloadAction<{ message: string }>) => {
          state.emailIrForwardCurveReport.loading = false;
          state.emailIrForwardCurveReport.data = action.payload;
          state.emailIrForwardCurveReport.successMessage =
            action.payload.message;
        },
      )
      .addCase(emailIrForwardCurveReport.rejected, (state, action) => {
        state.emailIrForwardCurveReport.loading = false;
        state.emailIrForwardCurveReport.error =
          action.payload || 'Failed to email IR Forward Curve Report';
      });
    // Currencies Market Data
    builder
      .addCase(currenciesMarketData.pending, (state) => {
        state.currenciesMarketData.loading = true;
        state.currenciesMarketData.error = null;
        state.currenciesMarketData.successMessage = null;
      })
      .addCase(
        currenciesMarketData.fulfilled,
        (
          state,
          action: PayloadAction<{ currencies: any[]; message: string }>,
        ) => {
          state.currenciesMarketData.loading = false;
          state.currenciesMarketData.data = action.payload.currencies;
          state.currenciesMarketData.successMessage = action.payload.message;
        },
      )
      .addCase(currenciesMarketData.rejected, (state, action) => {
        state.currenciesMarketData.loading = false;
        state.currenciesMarketData.error =
          action.payload || 'Failed to get Currencies';
      });
    // Fetch Filtered Currencies
    builder
      .addCase(filteredCurrenciesMarketData.pending, (state) => {
        state.filteredCurrencies.loading = true;
        state.filteredCurrencies.error = null;
        state.filteredCurrencies.successMessage = null;
      })
      .addCase(
        filteredCurrenciesMarketData.fulfilled,
        (
          state,
          action: PayloadAction<{ currencies: any[]; message: string }>,
        ) => {
          state.filteredCurrencies.loading = false;
          state.filteredCurrencies.data = action.payload.currencies;
          state.filteredCurrencies.successMessage = action.payload.message;
        },
      )
      .addCase(filteredCurrenciesMarketData.rejected, (state, action) => {
        state.filteredCurrencies.loading = false;
        state.filteredCurrencies.error =
          action.payload || 'Failed to get Filtered Currencies';
      });
    // IR Differential by Currency
    builder
      .addCase(fetchIRDifferential.pending, (state) => {
        state.irDifferential.loading = true;
        state.irDifferential.error = null;
        state.irDifferential.successMessage = null;
      })
      .addCase(
        fetchIRDifferential.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.irDifferential.loading = false;
          state.irDifferential.data = action.payload;
          state.irDifferential.successMessage =
            'IR Differential fetched successfully';
        },
      )
      .addCase(fetchIRDifferential.rejected, (state, action) => {
        state.irDifferential.loading = false;
        state.irDifferential.error =
          action.payload || 'Failed to fetch IR Differential';
      });
    // Commodities Indices Market Data
    builder
      .addCase(fetchCommoditiesIndices.pending, (state) => {
        state.commodityIndexList.loading = true;
        state.commodityIndexList.error = null;
      })
      .addCase(
        fetchCommoditiesIndices.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.commodityIndexList.loading = false;
          state.commodityIndexList.data = action.payload;
        },
      )
      .addCase(fetchCommoditiesIndices.rejected, (state, action) => {
        state.commodityIndexList.loading = false;
        state.commodityIndexList.error =
          action.payload || 'Failed to fetch Commodities Indices';
      });
    // Check Commodity Availability
    builder
      .addCase(checkCommodityAvailability.pending, (state) => {
        state.commodityAvailability.loading = true;
        state.commodityAvailability.error = null;
        state.commodityAvailability.successMessage = null;
      })
      .addCase(
        checkCommodityAvailability.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.commodityAvailability.loading = false;
          state.commodityAvailability.data = action.payload;
          state.commodityAvailability.successMessage =
            'Commodity availability checked successfully.';
        },
      )
      .addCase(checkCommodityAvailability.rejected, (state, action) => {
        state.commodityAvailability.loading = false;
        state.commodityAvailability.error =
          action.payload || 'Failed to check commodity availability.';
      });
    // Add Time Frequencies to Market Data Slice
    builder
      .addCase(fetchIRTimeFrequencies.pending, (state) => {
        state.timeFrequencies.loading = true;
        state.timeFrequencies.error = null;
        state.timeFrequencies.successMessage = null;
      })
      .addCase(
        fetchIRTimeFrequencies.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.timeFrequencies.loading = false;
          state.timeFrequencies.data = action.payload;
        },
      )
      .addCase(fetchIRTimeFrequencies.rejected, (state, action) => {
        state.timeFrequencies.loading = false;
        state.timeFrequencies.error =
          action.payload || 'Failed to fetch Time Frequencies';
      });
    // Fetch all Securities
    builder
      .addCase(fetchSecurities.pending, (state) => {
        state.securities.loading = true;
        state.securities.error = null;
      })
      .addCase(
        fetchSecurities.fulfilled,
        (
          state,
          action: PayloadAction<{
            securities: { id: number; label: string }[];
          }>,
        ) => {
          state.securities.loading = false;
          state.securities.data = action.payload.securities;
        },
      )
      .addCase(fetchSecurities.rejected, (state, action) => {
        state.securities.loading = false;
        state.securities.error =
          action.payload || 'Failed to fetch securities.';
      });
    // CDS Curve Report
    builder
      .addCase(downloadCDSCurveReport.pending, (state) => {
        state.cdsCurveReport.loading = true;
        state.cdsCurveReport.error = null;
      })
      .addCase(downloadCDSCurveReport.fulfilled, (state) => {
        state.cdsCurveReport.loading = false;
        state.cdsCurveReport.data = null;
        state.cdsCurveReport.successMessage =
          'CDS Curve Report ready for download';
      })
      .addCase(downloadCDSCurveReport.rejected, (state, action) => {
        state.cdsCurveReport.loading = false;
        state.cdsCurveReport.error = action.payload || null;
      });

    // Ir Differencial Currency Report
    builder
      .addCase(downloadIrDifferencialCurrencyReport.pending, (state) => {
        state.irDifferencialCurrencyReport.loading = true;
        state.irDifferencialCurrencyReport.error = null;
      })
      .addCase(downloadIrDifferencialCurrencyReport.fulfilled, (state) => {
        state.irDifferencialCurrencyReport.loading = false;
        state.irDifferencialCurrencyReport.data = null;
        state.irDifferencialCurrencyReport.successMessage =
          'Ir Differencial Currency Report ready for download';
      })
      .addCase(
        downloadIrDifferencialCurrencyReport.rejected,
        (state, action) => {
          state.irDifferencialCurrencyReport.loading = false;
          state.irDifferencialCurrencyReport.error = action.payload || null;
        },
      );

    // Historical Forward Curve Report
    builder
      .addCase(downloadHistoricalForwardCurveReport.pending, (state) => {
        state.historicalForwardCurveReport.loading = true;
        state.historicalForwardCurveReport.error = null;
      })
      .addCase(downloadHistoricalForwardCurveReport.fulfilled, (state) => {
        state.historicalForwardCurveReport.loading = false;
        state.historicalForwardCurveReport.data = null;
        state.historicalForwardCurveReport.successMessage =
          'Historical Forward Curve Report ready for download';
      })
      .addCase(
        downloadHistoricalForwardCurveReport.rejected,
        (state, action) => {
          state.historicalForwardCurveReport.loading = false;
          state.historicalForwardCurveReport.error = action.payload || null;
        },
      );

    // Hairy Chart Report
    builder
      .addCase(downloadHairyChartReport.pending, (state) => {
        state.hairyChartReport.loading = true;
        state.hairyChartReport.error = null;
      })
      .addCase(downloadHairyChartReport.fulfilled, (state) => {
        state.hairyChartReport.loading = false;
        state.hairyChartReport.data = null;
        state.hairyChartReport.successMessage =
          'Hairy Chart Report ready for download';
      })
      .addCase(downloadHairyChartReport.rejected, (state) => {
        state.hairyChartReport.loading = false;
        state.hairyChartReport.error = null;
      });

    // Fx Forward Curves
    builder
      .addCase(reportFxForwardCurves.pending, (state) => {
        state.fxForwardCurves.loading = true;
        state.fxForwardCurves.error = null;
      })
      .addCase(reportFxForwardCurves.fulfilled, (state) => {
        state.fxForwardCurves.loading = false;
        state.fxForwardCurves.data = null;
        state.fxForwardCurves.successMessage =
          'Fx Forward Curves Report ready for download';
      })
      .addCase(reportFxForwardCurves.rejected, (state, action) => {
        state.fxForwardCurves.loading = false;
        state.fxForwardCurves.error = action.payload || null;
      });

    // Commodity Forward Curve Report
    builder
      .addCase(downloadCommodityForwardCurveReport.pending, (state) => {
        state.commodityForwardCurveReport.loading = true;
        state.commodityForwardCurveReport.error = null;
      })
      .addCase(downloadCommodityForwardCurveReport.fulfilled, (state) => {
        state.commodityForwardCurveReport.loading = false;
        state.commodityForwardCurveReport.data = null;
        state.commodityForwardCurveReport.successMessage =
          'Commodity Forward Curve Report ready for download';
      })
      .addCase(
        downloadCommodityForwardCurveReport.rejected,
        (state, action) => {
          state.commodityForwardCurveReport.loading = false;
          state.commodityForwardCurveReport.error = action.payload || null;
        },
      );
  },
});

export const { clearValidation, clearSuccessMessages, clearErrorMessages } =
  marketDataSlice.actions;

export default marketDataSlice.reducer;
